<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark>
        <v-toolbar-title>단타의 신 · 매체 관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" outlined small @click="openRegistDialog">
          <v-icon left small>mdi-plus</v-icon>매체 등록
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-0">
        <div style="overflow-x: auto;">
        <v-data-table
          :headers="headers"
          :items="mediaList"
          :items-per-page="20"
          :footer-props="{'items-per-page-options':[20, 50, -1]}"
          class="elevation-0"
        >
          <template v-slot:item.is_active="{ item }">
            <v-chip small :color="item.is_active ? 'green' : 'orange'" dark>
              {{ item.is_active ? '게시' : '미게시' }}
            </v-chip>
          </template>
          <template v-slot:item.client_name="{ item }">
            <span class="font-weight-medium">{{ item.client_name || item.client_id || '-' }}</span>
          </template>
          <template v-slot:item.app_key="{ item }">
            <span class="text-body-2" style="font-family:monospace;">{{ item.app_key || '-' }}</span>
          </template>
          <template v-slot:item.media_type="{ item }">
            <v-icon x-small left>{{ item.media_type === 'app' ? 'mdi-cellphone' : item.media_type === 'web' ? 'mdi-web' : item.media_type === 'api' ? 'mdi-api' : 'mdi-dots-horizontal' }}</v-icon>
            {{ mediaTypeLabel(item.media_type) }}
          </template>
          <template v-slot:item.media_url="{ item }">
            <span v-if="item.media_type === 'app'" class="text-caption">{{ item.package_aos || item.media_url || '-' }}</span>
            <span v-else class="text-caption">{{ item.media_url || '-' }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openEditDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        </div>
        <v-container v-if="mediaList.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-television</v-icon>
          <p class="text-body-1 grey--text mt-3">등록된 매체가 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="680" scrollable>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">{{ isEdit ? 'mdi-television' : 'mdi-plus-circle' }}</v-icon>
          {{ isEdit ? '매체 수정' : '매체 등록' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <!-- 고객사 드롭다운 -->
          <v-select
            v-model="form.client_id"
            :items="clientOptions"
            item-text="client_name"
            item-value="client_id"
            label="연동 고객사 *"
            outlined dense hide-details class="mb-3"
            :disabled="isEdit"
            :loading="clientLoading"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.client_name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.client_id }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>

          <v-text-field v-model="form.media_name" label="매체 명칭 *" outlined dense hide-details class="mb-3"></v-text-field>

          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="form.media_id" label="매체 아이디 *" outlined dense class="mb-3" :disabled="isEdit" placeholder="clout, gameple" hint="직접 입력, 등록 후 변경 불가" persistent-hint></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="form.app_key" label="APP KEY (자동생성)" outlined dense hide-details class="mb-3" readonly disabled></v-text-field>
            </v-col>
          </v-row>

          <v-select v-model="form.media_type" :items="mediaTypeOptions" label="매체 타입 *" outlined dense hide-details class="mb-3" @change="onMediaTypeChange"></v-select>

          <!-- 매체 타입에 따라 URL/패키지 -->
          <template v-if="form.media_type === 'app'">
            <v-row dense>
              <v-col cols="6">
                <v-text-field v-model="form.package_aos" label="Android 패키지명" outlined dense hide-details class="mb-3" placeholder="com.example.app"></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="form.package_ios" label="iOS 번들 ID" outlined dense hide-details class="mb-3" placeholder="com.example.bundle"></v-text-field>
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <v-text-field v-model="form.media_url" label="사이트 URL" outlined dense hide-details class="mb-3" placeholder="https://example.com"></v-text-field>
          </template>

          <v-textarea v-model="form.description" label="설명" outlined dense hide-details class="mb-3" rows="2"></v-textarea>

          <v-divider class="mb-3"></v-divider>
          <v-text-field v-model="form.point_rate" label="1포인트 = ?원 (한화)" outlined dense class="mb-3" type="number" step="any" persistent-hint>
            <template v-slot:append>
              <span class="text-caption grey--text mt-1">원</span>
            </template>
          </v-text-field>
          <v-alert v-if="form.point_rate" dense text type="info" class="mb-3 body-2">
            1포인트 = <strong>{{ form.point_rate }}원</strong>
            &nbsp;|&nbsp; 100포인트 = <strong>{{ (form.point_rate * 100).toLocaleString() }}원</strong>
            &nbsp;|&nbsp; 10,000포인트 = <strong>{{ (form.point_rate * 10000).toLocaleString() }}원</strong>
          </v-alert>
          <v-divider class="mb-3"></v-divider>

          <!-- 배팅 참여 / 콜백 설정 -->
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon small left>mdi-swap-horizontal</v-icon>배팅 참여 / 콜백 설정
          </div>
          <v-select
            v-model="form.point_mode"
            :items="pointModeOptions"
            label="포인트 처리 방식 *"
            outlined dense hide-details class="mb-3"
            persistent-hint
          ></v-select>
          <v-alert dense text type="info" class="mb-3 body-2">
            <strong>remote</strong>: 매체사 포인트 시스템 (배팅 참여 동기 요청으로 잔액 차감)<br/>
            <strong>local</strong>: 단타의신 내부 포인트 사용 (배팅 참여 요청 없이 자체 처리)
          </v-alert>
          <v-text-field
            v-model="form.bet_callback_url"
            label="베팅 콜백 URL (필수)"
            outlined dense class="mb-3"
            placeholder="https://media.example.com/pub/callback_danta_action"
            persistent-hint
            hint="베팅 승인/확정 콜백 (bet_request/confirmed) — 리트라이 필수. remote 모드일 때 매체사가 잔액 체크/차감을 수행한다."
          ></v-text-field>
          <v-text-field
            v-model="form.state_callback_url"
            label="라운드 상태 알림 URL"
            outlined dense class="mb-3"
            placeholder="https://media.example.com/pub/callback_danta_state"
            persistent-hint
            hint="라운드 시작/종료/마감 등 알림형 이벤트 (round_started/closed) — 실패해도 무시."
          ></v-text-field>
          <v-text-field
            v-model="form.event_callback_url"
            label="이벤트 통합 콜백 URL"
            outlined dense class="mb-3"
            placeholder="https://media.example.com/pub/callback_danta_event"
            persistent-hint
            hint="단발성 이벤트 통합 채널. 현재 event_type=tutorial_reward 발송. 향후 추가 이벤트도 이 URL 로 전달. 빈값이면 발송 생략."
          ></v-text-field>
          <v-text-field
            v-model="form.callback_secret"
            label="HMAC 시크릿"
            outlined dense class="mb-3"
            placeholder="sec-xxxxxxxxxxxx"
            persistent-hint
            hint="배팅 참여/콜백 서명(X-Signature) 계산에 사용. 매체사와 공유하는 비밀키."
          >
            <template v-slot:append-outer>
              <v-btn x-small outlined @click="generateCallbackSecret">
                <v-icon left x-small>mdi-refresh</v-icon>재생성
              </v-btn>
            </template>
          </v-text-field>
          <v-divider class="mb-3"></v-divider>

          <!-- 튜토리얼 설정 — 신규 유저 진입 시 튜토리얼 페이지로 라우팅 + 완료 보상 -->
          <!-- 콜백 URL 은 위 '이벤트 통합 콜백' 으로 통합됨 -->
          <div class="subtitle-2 mb-2">튜토리얼 설정</div>
          <div class="d-flex align-center mb-3">
            <v-switch
              v-model="form.tutorial_enabled"
              inset hide-details class="mt-0 pt-0"
              :color="form.tutorial_enabled ? 'green' : 'grey'"
              :label="form.tutorial_enabled ? '활성 — 신규 유저 진입 시 튜토리얼 표시' : '비활성'"
            ></v-switch>
          </div>
          <v-text-field
            v-model.number="form.tutorial_reward"
            label="튜토리얼 완료 보상 (현금가, point)"
            outlined dense class="mb-3"
            type="number" min="0"
            :disabled="!form.tutorial_enabled"
            persistent-hint
            hint="유저가 튜토리얼을 완료했을 때 즉시 지급되는 보상 포인트. 0 이면 보상 없이 완료 기록만 남김. 발송 대상 URL 은 위 '이벤트 통합 콜백 URL'."
          ></v-text-field>
          <v-divider class="mb-3"></v-divider>

          <div class="d-flex align-center mb-3">
            <v-switch v-model="form.is_active" inset hide-details class="mt-0 pt-0" :color="form.is_active ? 'green' : 'grey'"></v-switch>
            <v-chip small :color="form.is_active ? 'green' : 'grey'" dark class="ml-2">
              <v-icon left x-small>{{ form.is_active ? 'mdi-check-circle' : 'mdi-pause-circle' }}</v-icon>
              {{ form.is_active ? '게시중' : '미게시' }}
            </v-chip>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">취소</v-btn>
          <v-btn color="primary" depressed @click="saveMedia">{{ isEdit ? '수정' : '등록' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
export default {
  name: 'Danta_MediaManage',
  data: () => ({
    dialog: false,
    isEdit: false,
    clientLoading: false,
    mediaList: [],
    clientOptions: [],
    headers: [
      { text: '매체 아이디', value: 'media_id', width: '120px' },
      { text: '매체 명칭', value: 'media_name', width: '140px' },
      { text: '고객사', value: 'client_name', width: '120px' },
      { text: 'APP KEY', value: 'app_key', width: '180px' },
      { text: '매체 타입', value: 'media_type', width: '90px' },
      { text: 'URL/패키지', value: 'media_url', width: '180px' },
      { text: '상태', value: 'is_active', width: '80px' },
      { text: '수정', value: 'actions', width: '60px', sortable: false },
    ],
    mediaTypeOptions: [
      { text: '웹', value: 'web' },
      { text: '앱', value: 'app' },
      { text: 'API', value: 'api' },
      { text: '기타', value: 'etc' },
    ],
    pointModeOptions: [
      { text: 'remote (매체사 포인트)', value: 'remote' },
      { text: 'local (내부 포인트)', value: 'local' },
    ],
    form: {},
  }),
  created() {
    this.resetForm();
    this.loadMediaList();
    this.loadClientOptions();
  },
  methods: {
    ...mapActions([
      'DANTA_GET_MEDIA_LIST',
      'DANTA_CREATE_MEDIA',
      'DANTA_UPDATE_MEDIA',
      'DANTA_DELETE_MEDIA',
      'DANTA_GET_CLIENT_LIST',
    ]),
    resetForm() {
      this.form = {
        media_id: '', media_name: '', client_id: '', app_key: '',
        media_type: 'web', media_url: '', package_aos: '', package_ios: '',
        description: '', point_rate: 1.0, is_active: true,
        point_mode: 'remote', bet_callback_url: '', state_callback_url: '',
        event_callback_url: '', callback_secret: '',
        tutorial_enabled: false, tutorial_reward: 0,
      };
    },
    generateRandomHash(length) {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
      return result;
    },
    async loadMediaList() {
      try {
        const body_data = await this.DANTA_GET_MEDIA_LIST({});
        this.mediaList = body_data.media_list || [];
      } catch (err) {
        console.error('매체 리스트 로드 실패:', err);
      }
    },
    async loadClientOptions() {
      this.clientLoading = true;
      try {
        const body_data = await this.DANTA_GET_CLIENT_LIST();
        this.clientOptions = body_data.client_list || [];
      } catch (err) {
        console.error('고객사 리스트 로드 실패:', err);
      } finally {
        this.clientLoading = false;
      }
    },
    openRegistDialog() {
      this.isEdit = false;
      this.resetForm();
      this.form.app_key = 'pub-' + this.generateRandomHash(16);
      this.form.callback_secret = 'sec-' + this.generateRandomHash(32);
      this.dialog = true;
    },
    generateCallbackSecret() {
      this.form.callback_secret = 'sec-' + this.generateRandomHash(32);
    },
    openEditDialog(item) {
      this.isEdit = true;
      this.form = JSON.parse(JSON.stringify(item));
      this.dialog = true;
    },
    onMediaTypeChange() {
      this.form.media_url = '';
      this.form.package_aos = '';
      this.form.package_ios = '';
    },
    mediaTypeLabel(type) {
      const map = { web: '웹', app: '앱', api: 'API', etc: '기타' };
      return map[type] || type;
    },
    async saveMedia() {
      if (!this.form.media_name || !this.form.client_id) {
        alert('매체 명칭과 고객사는 필수입니다.');
        return;
      }
      try {
        const data = JSON.parse(JSON.stringify(this.form));
        if (this.isEdit) {
          await this.DANTA_UPDATE_MEDIA(data);
        } else {
          await this.DANTA_CREATE_MEDIA(data);
        }
        this.dialog = false;
        await this.loadMediaList();
      } catch (err) {
        console.error(err);
        alert(this.isEdit ? '매체 수정에 실패했습니다.' : '매체 등록에 실패했습니다.');
      }
    },
  },
};
</script>
